import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { type ChecklistResponse, getChecklist } from "../lib/checklistApi";
import { Toast } from "../components/Toast";
import "./ChecklistPage.css";

export default function ChecklistPage() {
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState<ChecklistResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState<
    { message: string; type?: "error" | "success" | "info" } | null
  >(null);

  useEffect(() => {
    loadChecklist();
  }, []);

  const loadChecklist = async () => {
    setIsLoading(true);
    try {
      const response = await getChecklist();
      console.log("Resposta da API /checklist:", response);
      setChecklist(response);
    } catch (error: any) {
      console.error("Erro ao carregar checklist:", error);
      setToast({
        message: error.message || "Erro ao carregar checklist",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getPendingSteps = () => {
    return checklist?.data.steps.filter((step) =>
      step.status !== "completed"
    ) || [];
  };

  const getCompletedSteps = () => {
    return checklist?.data.steps.filter((step) =>
      step.status === "completed"
    ) || [];
  };

  const getStepTitle = (stepId: string): string => {
    const titles: Record<string, string> = {
      checklistKycSumsubStep: "Complete seu cadastro",
      checklistCpfDocumentStep: "Informar seu CPF",
      checklistTermsStep: "Revise nossos termos",
      checklistVerifyPhoneStep: "Confirmação de telefone",
    };
    return titles[stepId] || stepId;
  };

  if (isLoading) {
    return (
      <div className="checklist-page">
        <div className="checklist-loading">
          <div className="loading-spinner"></div>
          <p>Carregando checklist...</p>
        </div>
      </div>
    );
  }

  if (!checklist) {
    return (
      <div className="checklist-page">
        <div className="checklist-error">
          <p>Erro ao carregar checklist</p>
          <button onClick={loadChecklist}>Tentar novamente</button>
        </div>
      </div>
    );
  }

  return (
    <div className="checklist-page">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="checklist-container">
        <div className="checklist-header">
          <button
            type="button"
            className="checklist-back"
            onClick={() => navigate("/profile")}
          >
            &#8592; Voltar
          </button>
          <h1 className="checklist-title">Checklist de Progresso</h1>
          <div />
        </div>

        {/* Indicadores */}
        <div className="checklist-indicators">
          <div className="indicator-card">
            <div className="indicator-label">Progresso</div>
            <div className="indicator-value">
              {checklist.data.percentage.toFixed(1)}%
            </div>
            <div className="progress-bar">
              <div
                className="progress-bar-fill"
                style={{ width: `${checklist.data.percentage}%` }}
              >
              </div>
            </div>
          </div>

          <div className="indicator-card">
            <div className="indicator-label">Total de Passos</div>
            <div className="indicator-value">
              {checklist.data.totalDefinitions}
            </div>
          </div>

          <div className="indicator-card">
            <div className="indicator-label">Concluídos</div>
            <div className="indicator-value">
              {checklist.data.completedDefinitions}
            </div>
          </div>

          <div className="indicator-card">
            <div className="indicator-label">Pendentes</div>
            <div className="indicator-value">{getPendingSteps().length}</div>
          </div>
        </div>

        {/* Lista de Pendências */}
        <div className="checklist-section">
          <h2 className="section-title">
            Pendências ({getPendingSteps().length})
          </h2>
          {getPendingSteps().length === 0
            ? (
              <div className="empty-state">
                <p>Nenhuma pendência! Parabéns! 🎉</p>
              </div>
            )
            : (
              <div className="checklist-items">
                {getPendingSteps().map((step) => (
                  <div key={step.id} className="checklist-item pending">
                    <div className="item-content">
                      <div className="item-header">
                        <span className="item-title">
                          {getStepTitle(step.definition.id)}
                        </span>
                        <span className="item-badge status">{step.status}</span>
                      </div>
                      <div className="item-meta">
                        <span>ID: {step.definition.id}</span>
                        <span>Peso: {step.definition.weight}%</span>
                        <span>Nível: {step.definition.level}</span>
                        <span>País: {step.definition.country}</span>
                        {step.definition.levelName && (
                          <span>Level Name: {step.definition.levelName}</span>
                        )}
                      </div>
                    </div>
                    {step.definition.id === "checklistKycSumsubStep" &&
                      step.definition.levelName && (
                      <button
                        className="action-button"
                        onClick={() =>
                          navigate(`/kyc/${step.definition.levelName}`)}
                      >
                        Atualizar cadastro
                      </button>
                    )}
                    {step.definition.id === "checklistVerifyPhoneStep" &&
                      step.definition.levelName && (
                      <button
                        className="action-button"
                        onClick={() =>
                          navigate(`/kyc/${step.definition.levelName}`)}
                      >
                        Verificar Telefone
                      </button>
                    )}
                    {step.definition.id === "checklistCpfDocumentStep" && (
                      <button
                        className="action-button"
                        onClick={() =>
                          navigate("/checklist/cpf")}
                      >
                        Informar CPF
                      </button>
                    )}
                    {step.definition.id === "checklistTermsStep" && (
                      <button
                        className="action-button"
                        onClick={() => navigate("/checklist/terms")}
                      >
                        Revisar Termos
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
        </div>

        {/* Lista de Concluídos */}
        {getCompletedSteps().length > 0 && (
          <div className="checklist-section">
            <h2 className="section-title">
              Concluídos ({getCompletedSteps().length})
            </h2>
            <div className="checklist-items">
              {getCompletedSteps().map((step) => (
                <div key={step.id} className="checklist-item completed">
                  <div className="item-content">
                    <div className="item-header">
                      <span className="item-title">
                        {getStepTitle(step.definition.id)}
                      </span>
                      <span className="item-badge status">{step.status}</span>
                    </div>
                    <div className="item-meta">
                      <span>ID: {step.definition.id}</span>
                      <span>Peso: {step.definition.weight}%</span>
                      <span>Nível: {step.definition.level}</span>
                      <span>País: {step.definition.country}</span>
                      {step.definition.levelName && (
                        <span>Level Name: {step.definition.levelName}</span>
                      )}
                    </div>
                  </div>
                  {step.definition.id === "checklistKycSumsubStep" &&
                    step.definition.levelName && (
                    <button
                      className="action-button"
                      onClick={() =>
                        navigate(`/kyc/${step.definition.levelName}`)}
                    >
                      Ver KYC
                    </button>
                  )}
                  {step.definition.id === "checklistVerifyPhoneStep" &&
                    step.definition.levelName && (
                    <button
                      className="action-button"
                      onClick={() =>
                        navigate(`/kyc/${step.definition.levelName}`)}
                    >
                      Ver Telefone
                    </button>
                  )}
                  {step.definition.id === "checklistCpfDocumentStep" && (
                    <button
                      className="action-button"
                      onClick={() => navigate("/checklist/cpf")}
                    >
                      Ver CPF
                    </button>
                  )}
                  {step.definition.id === "checklistTermsStep" && (
                    <button
                      className="action-button"
                      onClick={() => navigate("/checklist/terms")}
                    >
                      Ver Termos
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dump da Resposta */}
        <div className="checklist-section">
          <h2 className="section-title">Dump da Resposta da API</h2>
          <div className="json-dump">
            <pre>{JSON.stringify(checklist, null, 2)}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}
